import { CopyableId } from '@noor/ui';
import {
  entityModifiers,
  firstNonEmpty,
  shortId,
  type ModerationEntity,
  type Translate,
} from '@/entities/moderation';
import { fieldLabel, formatFieldValue } from '../../lib/field-labels';
import { patchFieldEntries } from '../../lib/patch-values';
import { PatchValue } from './patch-value';

/** Fields already covered by the dedicated sections of a version column. */
const COVERED_DIFF_KEYS = new Set([
  'week_schedule',
  'name',
  'description',
  'images',
  'price',
  'modifiers',
  // Rendered by `ModifierChangesBlock` beside the modifier groups.
  'modifier_links',
  'restaurant_category_ids',
  'restaurant_products',
  'modifier_option_states',
  'merchant_id',
  'restaurant_id',
  'updated_at',
  'likes_count',
  'rating',
  'total_order_count',
  'pending_change_request_count',
  'submitted_for_review_at',
  'product_ids',
  'buy_quantity',
  'get_quantity',
  // Shown as the name on the `product_category_id` row.
  'product_category_name',
]);
const COORDINATE_KEYS = new Set(['latitude', 'longitude']);

/**
 * Generic changed-fields block: one row per `keys` entry not covered by a
 * dedicated section, with this column's value. Every column of the compare
 * grid gets the same key list (rows align side by side); `highlightKeys`
 * marks the values this column changes.
 */
export function ChangedFieldsSection({
  own,
  keys,
  highlightKeys,
  hiddenKeys,
  title,
  peers = [],
  tone = 'new',
  changedUnion,
  showUnchanged = true,
  onToggleUnchanged,
  t,
}: {
  own: ModerationEntity;
  keys: readonly string[];
  highlightKeys: ReadonlySet<string>;
  hiddenKeys?: ReadonlySet<string>;
  title?: string;
  /** Other versions of the entity — used to name ids this column only carries. */
  peers?: readonly (ModerationEntity | null | undefined)[];
  /** `old` marks the previous value amber (live column); `new` marks it red. */
  tone?: 'new' | 'old';
  /**
   * Keys changed by ANY compared version. With it, `showUnchanged = false`
   * folds the other rows away — the same rows in every column, so the grid
   * stays aligned.
   */
  changedUnion?: ReadonlySet<string> | undefined;
  showUnchanged?: boolean;
  onToggleUnchanged?: () => void;
  t: Translate;
}) {
  const allRows = [
    ...(keys.some((key) => COORDINATE_KEYS.has(key)) ? ['coordinates'] : []),
    ...keys.filter(
      (key) =>
        !COVERED_DIFF_KEYS.has(key) && !COORDINATE_KEYS.has(key) && !hiddenKeys?.has(key),
    ),
  ];
  if (allRows.length === 0) return null;
  const isChanged = (key: string) =>
    key === 'coordinates'
      ? [...COORDINATE_KEYS].some((coordinate) => changedUnion?.has(coordinate))
      : changedUnion?.has(key) === true;
  const foldable =
    changedUnion !== undefined &&
    changedUnion.size > 0 &&
    allRows.some((key) => !isChanged(key)) &&
    onToggleUnchanged !== undefined;
  const rows = foldable && !showUnchanged ? allRows.filter(isChanged) : allRows;
  const hiddenCount = allRows.length - rows.length;
  const changedClass = tone === 'old' ? 'text-warning' : 'text-destructive';
  const bag: Record<string, unknown> = own;
  const lookup = [own, ...peers].filter(
    (entity): entity is ModerationEntity => entity != null,
  );
  const groupName = (id: string) => modifierGroupName(lookup, id);

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">
          {title ?? t('detail.changedFields', 'Изменённые поля')}
        </h4>
        {foldable && (
          <button
            type="button"
            onClick={onToggleUnchanged}
            aria-expanded={showUnchanged}
            data-testid="toggle-unchanged-fields"
            className="text-xs font-medium text-primary hover:underline"
          >
            {showUnchanged
              ? t('detail.hideUnchanged', 'Скрыть неизменённые')
              : t('detail.showUnchanged', 'Показать неизменённые ({{n}})').replace(
                  '{{n}}',
                  String(hiddenCount),
                )}
          </button>
        )}
      </div>
      <dl className="mt-3 flex flex-col gap-2.5">
        {rows.map((key) => (
          <div key={key} className="flex flex-col gap-0.5 text-sm font-normal">
            <dt className="text-muted-foreground">{fieldLabel(key, t)}</dt>
            <dd
              className={
                highlightKeys.has(key) ||
                (key === 'coordinates' &&
                  [...COORDINATE_KEYS].some((coordinate) =>
                    highlightKeys.has(coordinate),
                  ))
                  ? `break-words ${changedClass}`
                  : 'break-words text-foreground'
              }
            >
              {key === 'coordinates' ? (
                <CopyableId
                  id={formatFieldValue(
                    key,
                    { latitude: bag.latitude, longitude: bag.longitude },
                    t,
                  )}
                  shorten={false}
                  copiedLabel={t('detail.copied', 'Скопировано')}
                  className={
                    highlightKeys.has(key) ||
                    [...COORDINATE_KEYS].some((coordinate) =>
                      highlightKeys.has(coordinate),
                    )
                      ? changedClass
                      : 'text-foreground'
                  }
                />
              ) : patchFieldEntries(key, bag[key]) ? (
                <PatchValue
                  entries={patchFieldEntries(key, bag[key]) ?? []}
                  groupName={groupName}
                  t={t}
                />
              ) : (
                formatFieldValue(key, displayValueOf(key, bag, lookup), t)
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Category rows show the category name; the id is only the last resort. */
const CATEGORY_ID_KEYS = new Set(['product_category_id', 'category_id']);

function displayValueOf(
  key: string,
  bag: Record<string, unknown>,
  lookup: readonly ModerationEntity[],
): unknown {
  if (!CATEGORY_ID_KEYS.has(key)) return bag[key];
  const id = typeof bag[key] === 'string' ? bag[key] : '';
  if (id === '') return bag[key];
  const named = [bag, ...lookup].find(
    (candidate) =>
      candidate.product_category_name &&
      (candidate[key] === id || candidate.product_category_id === id),
  );
  return named?.product_category_name ?? shortId(id);
}

export function modifierGroupName(
  lookup: readonly ModerationEntity[],
  id: string,
): string | undefined {
  for (const entity of lookup) {
    const group = entityModifiers(entity).find((candidate) => candidate.id === id);
    const name = group?.name;
    const label = firstNonEmpty(name?.uz, name?.ru, name?.en);
    if (label !== undefined) return label;
  }
  return undefined;
}
